<template>
  <div class="col-12">
    <div class="card-body px-0 pb-2">
      <div class="container-fluid px-0">
        <div class="row mb-4 g-2">
          <div class="col-12 col-md-4">
            <label class="form-label visually-hidden">{{ t('attendance.date') }}</label>
            <input
              type="date"
              class="form-control form-control-sm"
              v-model="date"
              :max="new Date().toISOString().split('T')[0]"
              @change="fetchStudents"
            >
          </div>

          <div class="col-12 col-md-4">
            <label class="form-label visually-hidden">{{ t('attendance.class') }}</label>
            <select
              class="form-select form-select-sm"
              v-model="classId"
              @change="fetchClassGroups"
            >
              <option value="">{{ t('attendance.select_class') }}</option>
              <option
                v-for="classItem in classes"
                :key="classItem.id"
                :value="classItem.id"
              >
                {{ classItem.name }}
              </option>
            </select>
          </div>

          <div class="col-12 col-md-4">
            <label class="form-label visually-hidden">{{ t('attendance.group') }}</label>
            <select
              class="form-select form-select-sm"
              v-model="groupId"
              @change="fetchStudents"
              :disabled="!classId"
            >
              <option value="">{{ t('attendance.select_group') }}</option>
              <option
                v-for="group in groups"
                :key="group.id"
                :value="group.id"
              >
                {{ group.name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="card mb-5" style="margin-top: 2rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">{{ t('attendance.record_attendance_title') }}</h6>
            </div>
          </div>
        </div>

        <div class="table-responsive p-4">
          <LoadingSpinner v-if="isLoading" :message="t('attendance.loading_students')" />

          <div v-else-if="students.length > 0 && Object.keys(attendances).length === students.length">
            <div class="d-block d-md-none">
              <div v-for="student in students" :key="'mobile-'+student.id" class="card mb-3">
                <div class="card-body">
                  <h6 class="mb-3 fw-medium">
                    {{ student.first_name }} {{ student.father_name }} {{ student.last_name }}
                  </h6>

                  <div class="mb-3">
                    <label class="form-label small">{{ t('attendance.status') }}</label>
                    <select
                      class="form-select form-select-sm borderless-input"
                      v-model="attendances[student.id].status"
                      :disabled="isSaving"
                    >
                      <option value="present">{{ t('attendance.present') }}</option>
                      <option value="absent">{{ t('attendance.absent') }}</option>
                      <option value="late">{{ t('attendance.late') }}</option>
                      <option value="excused">{{ t('attendance.excused') }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="mt-4 d-flex flex-column gap-2">
                <button
                  @click="saveAttendance"
                  class="btn btn-primary btn-sm"
                  :disabled="!date || !classId || students.length === 0 || isSaving"
                >
                  <span v-if="isSaving">
                    <i class="fas fa-spinner fa-spin me-2"></i>
                    {{ t('attendance.saving') }}
                  </span>
                  <span v-else>
                    <i class="fas fa-save ms-2"></i>
                    {{ t('attendance.save_attendance') }}
                  </span>
                </button>
                <button
                  @click="handleCancel"
                  class="btn btn-outline-secondary btn-sm"
                  :disabled="isSaving"
                >
                  <i class="fas fa-times ms-2"></i>
                  {{ t('attendance.cancel') }}
                </button>
              </div>
            </div>

            <div class="d-none d-md-block">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('attendance.student_name') }}</th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('attendance.status') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="student in students" :key="student.id">
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">
                            {{ student.first_name }} {{ student.father_name }} {{ student.last_name }}
                          </h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <select
                        class="form-select borderless-input"
                        v-model="attendances[student.id].status"
                        style="min-width: 120px;"
                        :disabled="isSaving"
                      >
                        <option value="present">{{ t('attendance.present') }}</option>
                        <option value="absent">{{ t('attendance.absent') }}</option>
                        <option value="late">{{ t('attendance.late') }}</option>
                        <option value="excused">{{ t('attendance.excused') }}</option>
                      </select>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="mt-4 d-flex gap-2">
                <button
                  @click="saveAttendance"
                  class="btn btn-primary btn-sm flex-grow-1"
                  :disabled="!date || !classId || students.length === 0 || isSaving"
                >
                  <span v-if="isSaving">
                    <i class="fas fa-spinner fa-spin me-2"></i>
                    {{ t('attendance.saving') }}
                  </span>
                  <span v-else>
                    <i class="fas fa-save ms-2"></i>
                    {{ t('attendance.save_attendance') }}
                  </span>
                </button>
                <button
                  @click="handleCancel"
                  class="btn btn-outline-secondary btn-sm flex-grow-1"
                  :disabled="isSaving"
                >
                  <i class="fas fa-times ms-2"></i>
                  {{ t('attendance.cancel') }}
                </button>
              </div>
            </div>
          </div>

          <template v-else-if="!isLoading && students.length > 0">
            <div class="text-center py-4">
              <div class="spinner-border text-primary spinner-border-sm" role="status">
                <span class="visually-hidden">{{ t('attendance.loading') }}</span>
              </div>
              <p class="mt-2 text-sm">{{ t('attendance.preparing_attendance') }}</p>
            </div>
          </template>

          <template v-else-if="!isLoading">
            <div class="text-center py-4 text-muted">
              <i class="fas fa-users-slash fa-2x mb-2"></i>
              <p class="text-sm">{{ t('attendance.no_students') }}</p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import api from '@/api';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const store = useStore();
const currentUser = computed(() => store.state?.user || null);
const isTeacher = computed(() => (currentUser.value?.role?.slug || '').toLowerCase() === 'teacher');

const { t } = useI18n();
const router = useRouter();
const toast = useToast();

const date = ref(new Date().toISOString().split('T')[0]);
const classId = ref('');
const groupId = ref('');
const classes = ref([]);
const groups = ref([]);
const students = ref([]);
const attendances = ref({});
const isLoading = ref(false);
const isSaving = ref(false);

const fetchClasses = async () => {
  try {
    let response;

    if (isTeacher.value && currentUser.value?.id) {
      try {
        response = await api.get(`/teachers/${currentUser.value.id}/classes`);
      } catch (e) {
        response = await api.get('/classes', { params: { teacher_id: currentUser.value.id } });
      }
    } else {
   response = await api.get('/classes', {
    params: {
      all: true
    }
  });    }

    const payload = response?.data;
    let list = [];

    if (Array.isArray(payload?.data)) list = payload.data;
    else if (Array.isArray(payload?.classes)) list = payload.classes;
    else if (Array.isArray(payload)) list = payload;
    else list = [];

    classes.value = list.map(c => c?.class ?? c);

    if (classId.value && !classes.value.some(c => String(c.id) === String(classId.value))) {
      classId.value = '';
      groups.value = [];
      groupId.value = '';
      students.value = [];
      attendances.value = {};
    }
  } catch (error) {
    toast.error(t('attendance.load_failed_classes'));
    console.error('Error fetching classes:', error);
    classes.value = [];
  }
};

const fetchClassGroups = async () => {
  groupId.value = '';

  if (!classId.value) {
    groups.value = [];
    students.value = [];
    attendances.value = {};
    return;
  }

  try {
    const response = await api.get(`/classes/${classId.value}/groups`);
    const raw = response?.data?.data ?? response?.data ?? [];
    groups.value = (Array.isArray(raw) ? raw : []).map(g => g?.group ?? g);
    await fetchStudents();
  } catch (error) {
    toast.error(t('attendance.load_failed_groups'));
    console.error("Error fetching class groups:", error);
    groups.value = [];
    students.value = [];
    attendances.value = {};
  }
};

const extractStudent = (item) => {
  if (!item) return null;

  const nested = item.student ?? item.user ?? item.profile ?? item.member ?? item.pivot?.student ?? null;
  const s = nested ?? item;

  const id = s?.id ?? item?.student_id ?? null;
  if (!id) return null;

  return {
    id,
    first_name: String(s?.first_name ?? item?.first_name ?? '').trim(),
    father_name: String(s?.father_name ?? item?.father_name ?? '').trim(),
    last_name: String(s?.last_name ?? item?.last_name ?? '').trim(),
    current_academic_year_id: s?.current_academic_year_id ?? item?.current_academic_year_id ?? null
  };
};

const fetchStudents = async () => {
  if (!classId.value) {
    students.value = [];
    attendances.value = {};
    return;
  }

  isLoading.value = true;
  students.value = [];
  attendances.value = {};

  try {
    const response = await api.get('/students', {
      params: {
        class_id: classId.value,
        group_id: groupId.value || undefined,
        per_page: 1000
      }
    });

    const raw = Array.isArray(response?.data?.data) ? response.data.data : [];
    const normalized = raw.map(extractStudent).filter(Boolean);

    const seen = new Set();
    students.value = normalized.filter((student) => {
      if (seen.has(student.id)) return false;
      seen.add(student.id);
      return true;
    });

    students.value.forEach((student) => {
      attendances.value[student.id] = {
        student_id: student.id,
        status: 'present',
        notes: ''
      };
    });
  } catch (error) {
    toast.error(t('attendance.load_failed_students'));
    console.error("Error fetching students:", error);
  } finally {
    isLoading.value = false;
  }
};

const saveAttendance = async () => {
  if (!date.value || students.value.length === 0) {
    toast.warning(t('attendance.fill_required_fields'));
    return;
  }

  isSaving.value = true;

  try {
    const checkResponse = await api.get('/attendances', {
      params: {
        date: date.value,
        class_id: classId.value,
        group_id: groupId.value || undefined
      }
    });

    if ((checkResponse.data?.data || []).length > 0) {
      toast.warning(t('attendance.duplicate_attendance'));
    }

    const attendanceData = Object.values(attendances.value).filter(att => att.student_id);

    const response = await api.post('/attendances', {
      date: date.value,
      class_id: classId.value,
      group_id: groupId.value || null,
      attendances: attendanceData
    });

    toast.success(response.data.message || t('attendance.record_attendance_success'));
    router.push('/attendances');
  } catch (error) {
    let errorMsg = t('attendance.record_attendance_failed');

    if (error.response?.data?.message) {
      errorMsg = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMsg = error.response.data.error;
    } else if (error.response?.data?.errors) {
      const errs = error.response.data.errors;
      for (const field in errs) {
        toast.error(errs[field][0]);
      }
      isSaving.value = false;
      return;
    }

    toast.error(errorMsg);
    console.error("Error saving attendance:", error);
  } finally {
    isSaving.value = false;
  }
};

const handleCancel = () => {
  if (!isSaving.value) {
    router.push('/attendances');
  }
};

onMounted(() => {
  fetchClasses();
});
</script>